var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { Property, Complex, ChildProperty, Collection, extend, Browser, createElement } from '@syncfusion/ej2-base';
import { Font, Border } from '../../common/model/base';
import { rotateTextSize, firstToLowerCase, valueToCoefficient, inside, isBreakLabel, isZoomSet, getTitle, getElement, appendChildElement, createTemplate, measureElementRect, textTrim } from '../../common/utils/helper';
import { Size, Rect, measureText } from '@syncfusion/ej2-svg-base';
import { DoubleRange } from '../utils/double-range';
import { Double } from '../axis/double-axis';
import { axisRangeCalculated } from '../../common/model/constants';
import { StripLineSettings, MultiLevelLabels, LabelBorder, ScrollbarSettings } from '../model/chart-base';
import { textWrap } from '../../common/utils/helper';
import { isNullOrUndefined } from '@syncfusion/ej2-base';
/**
 * Configures the `rows` of the chart to create multiple vertical rows within the chart area.
 */
export class Row extends ChildProperty {
    constructor() {
        /**
         * The height of the row as a string accepts input both as '100px' and '100%'.
         * If specified as '100%', the row renders to the full height of its chart.
         *
         * @default '100%'
         */
        super(...arguments);
        /** @private */
        this.axes = [];
        /** @private */
        this.nearSizes = [];
        /** @private */
        this.farSizes = [];
        /** @private */
        this.insideFarSizes = [];
        /** @private */
        this.insideNearSizes = [];
    }
    /**
     * Measure the row size.
     *
     * @param {Axis} axis - The axis for which to measure the row size.
     * @param {number} scrollBarHeight - The height of the scrollbar.
     * @param {Row | Column} definition - The definition of the row or column.
     * @param {Chart} chart - The chart instance.
     * @returns {void}
     * @private
     */
    computeSize(axis, scrollBarHeight, definition, chart) {
        let width = 0;
        const innerPadding = 5;
        if (axis.visible && axis.internalVisibility) {
            width += (axis.findTickSize(axis.crossInAxis) + ((axis.scrollbarSettings.position === 'Right' || axis.scrollbarSettings.position === 'Left') ? 0 : scrollBarHeight) +
                axis.findLabelSize(axis.crossInAxis, innerPadding, definition, chart) + axis.lineStyle.width * 0.5);
        }
        if (axis.isAxisOpposedPosition) {
            this.farSizes.push(width);
        }
        else {
            this.nearSizes.push(width);
        }
    }
}
__decorate([
    Property('100%')
], Row.prototype, "height", void 0);
__decorate([
    Complex({}, Border)
], Row.prototype, "border", void 0);
/**
 * Configures the `columns` of the chart to create multiple horizontal columns within the chart area.
 */
export class Column extends ChildProperty {
    constructor() {
        /**
         * The width of the column as a string accepts input both as '100px' and '100%'.
         * If specified as '100%', the column renders to the full width of its chart.
         *
         * @default '100%'
         */
        super(...arguments);
        /** @private */
        this.axes = [];
        /** @private */
        this.nearSizes = [];
        /** @private */
        this.farSizes = [];
        /** @private */
        this.insideFarSizes = [];
        /** @private */
        this.insideNearSizes = [];
        /** @private */
        this.padding = 0;
    }
    /**
     * Measure the column size
     *
     * @returns {void}
     * @private
     */
    computeSize(axis, scrollBarHeight, definition, chart) {
        let height = 0;
        const innerPadding = 5;
        if (axis.visible && axis.internalVisibility) {
            height += (axis.findTickSize(axis.crossInAxis) + ((axis.scrollbarSettings.position === 'Top' || axis.scrollbarSettings.position === 'Bottom') ? 0 : scrollBarHeight) +
                axis.findLabelSize(axis.crossInAxis, innerPadding, definition, chart) + axis.lineStyle.width * 0.5);
        }
        if (axis.isAxisOpposedPosition) {
            this.farSizes.push(height);
        }
        else {
            this.nearSizes.push(height);
        }
    }
}
__decorate([
    Property('100%')
], Column.prototype, "width", void 0);
__decorate([
    Complex({}, Border)
], Column.prototype, "border", void 0);
/**
 * Configures the major grid lines in the axis, allowing for the setting of properties such as color, width, and dashArray to define how the grid lines appear on the chart.
 */
export class MajorGridLines extends ChildProperty {
}
__decorate([
    Property(1)
], MajorGridLines.prototype, "width", void 0);
__decorate([
    Property('')
], MajorGridLines.prototype, "dashArray", void 0);
__decorate([
    Property(null)
], MajorGridLines.prototype, "color", void 0);
/**
 * Configures the minor grid lines in the axis, allowing for the setting of properties such as color, width, and dashArray to define how the grid lines appear on the chart.
 */
export class MinorGridLines extends ChildProperty {
}
__decorate([
    Property(0.7)
], MinorGridLines.prototype, "width", void 0);
__decorate([
    Property('')
], MinorGridLines.prototype, "dashArray", void 0);
__decorate([
    Property(null)
], MinorGridLines.prototype, "color", void 0);
/**
 * Configures the axis line of a chart, allowing customization of the line's appearance, including its color, width, and dashArray.
 */
export class AxisLine extends ChildProperty {
}
__decorate([
    Property(1)
], AxisLine.prototype, "width", void 0);
__decorate([
    Property('')
], AxisLine.prototype, "dashArray", void 0);
__decorate([
    Property(null)
], AxisLine.prototype, "color", void 0);
/**
 * Configures the major tick lines in the axis, allowing for the setting of properties such as color, width, and height to define how the tick lines appear on the chart.
 */
export class MajorTickLines extends ChildProperty {
}
__decorate([
    Property(1)
], MajorTickLines.prototype, "width", void 0);
__decorate([
    Property(5)
], MajorTickLines.prototype, "height", void 0);
__decorate([
    Property(null)
], MajorTickLines.prototype, "color", void 0);
/**
 * Configures the minor tick lines in the axis, allowing for the setting of properties such as color, width, and height to define how the tick lines appear on the chart.
 */
export class MinorTickLines extends ChildProperty {
}
__decorate([
    Property(0.7)
], MinorTickLines.prototype, "width", void 0);
__decorate([
    Property(5)
], MinorTickLines.prototype, "height", void 0);
__decorate([
    Property(null)
], MinorTickLines.prototype, "color", void 0);
/**
 * Configures the crosshair tooltip for the chart, allowing customization of the tooltip's appearance and content during crosshair interactions.
 */
export class CrosshairTooltip extends ChildProperty {
}
__decorate([
    Property(false)
], CrosshairTooltip.prototype, "enable", void 0);
__decorate([
    Property(null)
], CrosshairTooltip.prototype, "fill", void 0);
__decorate([
    Complex({ fontFamily: null, size: null, fontStyle: null, fontWeight: null, color: null }, Font)
], CrosshairTooltip.prototype, "textStyle", void 0);
/**
 * The Axis class configures the axes in the chart. It provides various properties to customize the appearance and behavior of chart axes, including settings for labels, grid lines, ticks, and more.
 *
 * @public
 */
export class Axis extends ChildProperty {
    constructor(parent, propName, defaultValue, isArray) {
        super(parent, propName, defaultValue, isArray);
        /** @private */
        this.visibleLabels = [];
        /** @private */
        this.series = [];
        /** @private */
        this.rect = new Rect(undefined, undefined, 0, 0);
        /** @private */
        this.axisBottomLine = null;
        /** @private */
        this.intervalDivs = [10, 5, 2, 1];
        /** @private */
        this.isStack100 = false;
        /** @private */
        this.crossAt = null;
        /** @private */
        this.updatedRect = null;
        /** @private */
        this.multiLevelLabelHeight = 0;
        /** @private */
        this.isChart = true;
        /** @private */
        this.isIntervalInDecimal = true;
        /** @private */
        this.titleCollection = [];
        /** @private */
        this.titleSize = new Size(0, 0);
        /**
         * Task: BLAZ-2044
         * This property used to hide the axis when series hide from legend click
         *
         * @private
         */
        this.internalVisibility = true;
        /**
         * This property is used to place the vertical axis in opposed position and horizontal axis in inversed
         * when RTL is enabled in chart
         *
         * @private */
        this.isRTLEnabled = false;
        this.angle = this.labelRotation;
    }
    /**
     * The function used to find tick size.
     *
     * @param {Axis} crossAxis - The cross axis for which to find the tick size.
     * @returns {number} - The tick line size.
     * @private
     */
    findTickSize(crossAxis) {
        if (this.tickPosition === 'Inside') {
            return 0;
        }
        if (crossAxis && (!crossAxis.visibleRange || this.isInside(crossAxis.visibleRange))) {
            return 0;
        }
        return this.majorTickLines.height;
    }
    /**
     * The function used to find axis position.
     *
     * @returns {number}
     * @private
     */
    isInside(range) {
        return (inside(this.crossAt, range) ||
            (!this.isAxisOpposedPosition && this.crossAt >= range.max) || (this.isAxisOpposedPosition && this.crossAt <= range.min));
    }
    /**
     * The function used to find label Size.
     *
     * @param {Axis} crossAxis - The cross axis for which to find the label size.
     * @param {number} innerPadding - The inner padding.
     * @param {Row | Column} definition - The row or column definition.
     * @param {Chart} chart - The chart instance.
     * @returns {number} - The label size.
     * @private
     */
    findLabelSize(crossAxis, innerPadding, definition, chart) {
        let titleSize = 0;
        const isHorizontal = this.orientation === 'Horizontal';
        if (this.title) {
            const angle = this.titleRotation;
            if ((isNullOrUndefined(angle))) {
                this.titleSize = measureText(this.title, this.titleStyle, chart.themeStyle.axisTitleFont);
                titleSize = this.titleSize.height + innerPadding;
            }
            else {
                this.titleSize = rotateTextSize(this.titleStyle, this.title, angle, chart, chart.themeStyle.axisTitleFont);
                titleSize = (this.orientation === 'Vertical' ? this.titleSize.width : this.titleSize.height) + innerPadding;
            }
            if (this.rect.width || this.rect.height) {
                const length = isHorizontal ? this.rect.width : this.rect.height;
                this.titleCollection = getTitle(this.title, this.titleStyle, length, chart.enableRtl, chart.themeStyle.axisTitleFont);
                titleSize = (titleSize * this.titleCollection.length);
            }
        }
        let diff;
        let value;
        let labelSize = titleSize + innerPadding + this.titlePadding + this.labelPadding +
            ((this.orientation === 'Vertical') ? this.maxLabelSize.width : this.maxLabelSize.height) + this.multiLevelLabelHeight;
        if (crossAxis && this.placeNextToAxisLine) {
            const range = crossAxis.visibleRange;
            const size = (crossAxis.orientation === 'Horizontal') ? crossAxis.rect.width : crossAxis.rect.height;
            if (!range || !size) {
                return 0;
            }
            else if (this.isInside(range)) {
                value = this.findDifference(crossAxis);
                diff = (value) * (size / range.delta);
                diff = (value) * ((size - (diff < labelSize ? (labelSize - diff) : 0)) / range.delta);
                labelSize = (diff < labelSize) ? (labelSize - diff) : 0;
            }
        }
        const titlePadding = ((this.title !== '' && this.titlePadding !== 5) ? this.titlePadding : 0);
        if (this.isAxisOpposedPosition) {
            definition.insideFarSizes.push(labelSize);
        }
        else {
            definition.insideNearSizes.push(labelSize);
        }
        if (this.labelPosition === 'Inside') {
            innerPadding = (chart.axes.indexOf(this) > -1) ? -5 : 5;
            if ((this.isAxisOpposedPosition && definition.farSizes.length < 1) ||
                (!this.isAxisOpposedPosition && definition.nearSizes.length < 1)) {
                return titleSize + innerPadding + titlePadding;
            }
            else {
                return this.isAxisOpposedPosition ? titleSize + innerPadding + titlePadding + this.labelPadding +
                    ((this.orientation === 'Vertical') ? this.maxLabelSize.width : this.maxLabelSize.height) +
                    this.multiLevelLabelHeight : titleSize + innerPadding + titlePadding;
            }
        }
        return labelSize;
    }
    /**
     * The function used to find axis position.
     *
     * @returns {void}
     * @private
     */
    updateCrossValue() {
        let value = this.crossAt;
        if (value === null || !this.isInside(this.crossInAxis.visibleRange)) {
            this.updatedRect = this.rect;
            return null;
        }
        const range = this.crossInAxis.visibleRange;
        if (!this.isAxisOpposedPosition) {
            if (this.crossAt > range.max) {
                value = range.max;
            }
        }
        else {
            if (this.crossAt < range.min) {
                value = range.min;
            }
        }
        this.updatedRect = extend({}, this.rect, null, true);
        if (this.orientation === 'Horizontal') {
            value = this.crossInAxis.rect.height - (valueToCoefficient(value, this.crossInAxis) * this.crossInAxis.rect.height);
            this.updatedRect.y = this.crossInAxis.rect.y + value;
        }
        else {
            value = valueToCoefficient(value, this.crossInAxis) * this.crossInAxis.rect.width;
            this.updatedRect.x = this.crossInAxis.rect.x + value;
        }
    }
    findDifference(crossAxis) {
        let value = 0;
        if (this.isAxisOpposedPosition) {
            value = crossAxis.isAxisInverse ? crossAxis.visibleRange.min : crossAxis.visibleRange.max;
        }
        else {
            value = crossAxis.isAxisInverse ? crossAxis.visibleRange.max : crossAxis.visibleRange.min;
        }
        return Math.abs(this.crossAt - value);
    }
    /**
     * Calculate the visible range for the axis.
     *
     * @returns {void}
     * @private
     */
    calculateVisibleRangeOnZooming() {
        if (isZoomSet(this)) {
            const baseRange = this.actualRange;
            let start;
            let end;
            if (!this.isAxisInverse) {
                start = this.actualRange.min + this.zoomPosition * this.actualRange.delta;
                end = start + this.zoomFactor * this.actualRange.delta;
            }
            else {
                start = this.actualRange.max - (this.zoomPosition * this.actualRange.delta);
                end = start - (this.zoomFactor * this.actualRange.delta);
            }
            if (start < baseRange.min) {
                end = end + (baseRange.min - start);
                start = baseRange.min;
            }
            if (end > baseRange.max) {
                start = start - (end - baseRange.max);
                end = baseRange.max;
            }
            this.doubleRange = new DoubleRange(start, end);
            this.visibleRange = { min: this.doubleRange.start, max: this.doubleRange.end,
                delta: this.doubleRange.delta, interval: this.visibleRange.interval };
        }
    }
    /**
     * Triggers the event.
     *
     * @returns {void}
     * @private
     */
    triggerRangeRender(chart, minimum, maximum, interval) {
        const argsData = {
            cancel: false, name: axisRangeCalculated, axis: this,
            minimum: minimum, maximum: maximum, interval: interval
        };
        chart.trigger(axisRangeCalculated, argsData);
        if (!argsData.cancel) {
            this.visibleRange = { min: argsData.minimum, max: argsData.maximum, interval: argsData.interval,
                delta: argsData.maximum - argsData.minimum };
        }
    }
    /**
     * Calculate padding for the axis.
     *
     * @returns {string}
     * @private
     */
    getRangePadding(chart) {
        let padding = this.rangePadding;
        if (padding !== 'Auto') {
            return padding;
        }
        switch (this.orientation) {
            case 'Horizontal':
                if (chart.requireInvertedAxis) {
                    padding = (this.isStack100 || this.baseModule.chart.stockChart ? 'Round' : 'Normal');
                }
                else {
                    padding = 'None';
                }
                break;
            case 'Vertical':
                if (!chart.requireInvertedAxis) {
                    padding = (this.isStack100 || this.baseModule.chart.stockChart ? 'Round' : 'Normal');
                }
                else {
                    padding = 'None';
                }
                break;
        }
        return padding;
    }
    /**
     * Calculate maximum label width for the axis.
     *
     * @param {Chart} chart - The chart for which to calculate the maximum label width.
     * @returns {void}
     * @private
     */
    getMaxLabelWidth(chart) {
        let pointX;
        let previousEnd = 0;
        let isIntersect = false;
        let isAxisLabelBreak;
        this.angle = this.labelRotation;
        this.maxLabelSize = new Size(0, 0);
        const action = this.labelIntersectAction;
        let label;
        const isRotateWithTrim = action === 'Rotate45WithTrim' || action === 'Rotate90WithTrim';
        if (this.labelTemplate && !isNullOrUndefined(this.labelTemplate)) {
            this.getMaxTemplateLabelWidth(chart);
            return;
        }
        for (let i = 0, len = this.visibleLabels.length; i < len; i++) {
            label = this.visibleLabels[i];
            if (isRotateWithTrim && typeof label.text === 'string') {
                label.text = label.originalText;
            }
            isAxisLabelBreak = isBreakLabel(label.originalText);
            if (isAxisLabelBreak) {
                label.size = measureText(label.originalText.replace(/<br>/g, ' '), this.labelStyle, chart.themeStyle.axisLabelFont);
                label.breakLabelSize = measureText(this.enableTrim ? label.text.join('<br>') : label.originalText, this.labelStyle, chart.themeStyle.axisLabelFont);
            }
            else if (this.enableWrap) {
                const maximumLabelHeight = chart.initialClipRect.height / this.visibleLabels.length;
                label.text = textWrap(label.text, this.maximumLabelWidth, this.labelStyle, chart.enableRtl, null, null, chart.themeStyle.axisLabelFont, this.orientation === 'Vertical' ? maximumLabelHeight : null);
                let maxTextWidth = 0;
                let maxTextHeight = 0;
                label.text.forEach((textLine) => {
                    const textSize = measureText(textLine, this.labelStyle, chart.themeStyle.axisLabelFont);
                    maxTextWidth = Math.max(maxTextWidth, textSize.width);
                    maxTextHeight += textSize.height;
                });
                label.size.width = maxTextWidth;
                label.size.height = maxTextHeight;
            }
            else {
                if ((this.angle === -90 || this.angle === 90 || this.angle === 270 || this.angle === -270) && this.orientation === 'Vertical') {
                    label.size = rotateTextSize(this.labelStyle, label.text, this.angle, chart, chart.themeStyle.axisLabelFont);
                }
                else {
                    label.size = measureText(label.text, this.labelStyle, chart.themeStyle.axisLabelFont);
                }
            }
            const width = isAxisLabelBreak ? label.breakLabelSize.width : label.size.width;
            if (width > this.maxLabelSize.width) {
                this.maxLabelSize.width = width;
                this.rotatedLabel = label.text;
            }
            const height = isAxisLabelBreak ? label.breakLabelSize.height : label.size.height;
            if (height > this.maxLabelSize.height) {
                this.maxLabelSize.height = height;
            }
            if (isAxisLabelBreak) {
                label.text = this.enableTrim ? label.text : label.originalText.split('<br>');
            }
            if (action === 'None' || action === 'Hide' || action === 'Trim') {
                continue;
            }
            if ((action !== 'None' || this.angle % 360 === 0) && this.orientation === 'Horizontal' &&
                this.rect.width > 0 && !isIntersect) {
                const width1 = isAxisLabelBreak ? label.breakLabelSize.width : label.size.width;
                pointX = (valueToCoefficient(label.value, this) * this.rect.width) + this.rect.x;
                pointX -= width1 / 2;
                if (this.edgeLabelPlacement === 'Shift') {
                    if (i === 0 && pointX < this.rect.x) {
                        pointX = this.rect.x;
                    }
                    if (i === this.visibleLabels.length - 1 && ((pointX + width1) > (this.rect.x + this.rect.width))) {
                        pointX = this.rect.x + this.rect.width - width1;
                    }
                }
                const availableLabelWidth = this.labelPlacement === 'BetweenTicks' || this.visibleLabels.length === 1
                    ? this.rect.width / this.visibleLabels.length
                    : this.rect.width / (this.visibleLabels.length - 1);
                const isLabelWidthExceeded = width1 > availableLabelWidth;
                switch (action) {
                    case 'MultipleRows':
                        if (i > 0) {
                            this.findMultiRows(i, pointX, label, isAxisLabelBreak);
                        }
                        break;
                    case 'Rotate45':
                    case 'Rotate90':
                        if (i > 0 && (!this.isAxisInverse ? pointX <= previousEnd : pointX + width1 >= previousEnd)) {
                            this.angle = (action === 'Rotate45') ? 45 : 90;
                            isIntersect = true;
                        }
                        break;
                    case 'Rotate45WithTrim':
                    case 'Rotate90WithTrim': {
                        const isLabelIntersect = i > 0 &&
                            (!this.isAxisInverse
                                ? pointX <= previousEnd
                                : pointX + width1 >= previousEnd);
                        if (isLabelWidthExceeded || isLabelIntersect) {
                            this.angle = action === 'Rotate45WithTrim' ? 45 : 90;
                            isIntersect = true;
                        }
                        break;
                    }
                    default: {
                        if (isAxisLabelBreak) {
                            let result;
                            const result1 = [];
                            let str;
                            for (let index = 0; index < label.text.length; index++) {
                                result = textWrap(label.text[index], this.rect.width / this.visibleLabels.length, this.labelStyle, chart.enableRtl, null, null, chart.themeStyle.axisLabelFont);
                                if (result.length > 1) {
                                    for (let j = 0; j < result.length; j++) {
                                        str = result[j];
                                        result1.push(str);
                                    }
                                }
                                else {
                                    result1.push(result[0]);
                                }
                            }
                            label.text = result1;
                        }
                        else {
                            label.text = textWrap(label.text, this.rect.width / this.visibleLabels.length, this.labelStyle, chart.enableRtl, null, null, chart.themeStyle.axisLabelFont);
                        }
                        const height = (label.size.height * label.text.length);
                        if (height > this.maxLabelSize.height) {
                            this.maxLabelSize.height = height;
                        }
                        break;
                    }
                }
                previousEnd = this.isAxisInverse ? pointX : pointX + width1;
            }
        }
        if (isRotateWithTrim && isIntersect && this.angle !== 0 && this.orientation === 'Horizontal'
            && this.rect.width > 0 && this.visibleLabels.length > 0) {
            // BetweenTicks has one slot per label.
            // OnTicks has one interval between every two tick positions.
            const trimWidth = this.labelPlacement === 'BetweenTicks' || this.visibleLabels.length === 1
                ? this.rect.width / this.visibleLabels.length
                : this.rect.width / (this.visibleLabels.length - 1);
            this.maxLabelSize = new Size(0, 0);
            this.rotatedLabel = '';
            for (const visibleLabel of this.visibleLabels) {
                const isAxisLabelBreak = isBreakLabel(visibleLabel.originalText);
                if (isAxisLabelBreak) {
                    const lines = visibleLabel.originalText.split('<br>');
                    const trimmedLines = [];
                    let maxWidth = 0;
                    let totalHeight = 0;
                    for (const line of lines) {
                        const lineSize = measureText(line, this.labelStyle, chart.themeStyle.axisLabelFont);
                        const trimmedLine = lineSize.width > trimWidth
                            ? textTrim(trimWidth, line, this.labelStyle, chart.enableRtl, chart.themeStyle.axisLabelFont)
                            : line;
                        trimmedLines.push(trimmedLine);
                        const trimmedSize = measureText(trimmedLine, this.labelStyle, chart.themeStyle.axisLabelFont);
                        maxWidth = Math.max(maxWidth, trimmedSize.width);
                        totalHeight += trimmedSize.height;
                    }
                    visibleLabel.text = trimmedLines;
                    visibleLabel.breakLabelSize = new Size(maxWidth, totalHeight);
                }
                else if (typeof visibleLabel.text === 'string') {
                    const originalSize = measureText(visibleLabel.originalText, this.labelStyle, chart.themeStyle.axisLabelFont);
                    visibleLabel.text = originalSize.width > trimWidth
                        ? textTrim(trimWidth, visibleLabel.originalText, this.labelStyle, chart.enableRtl, chart.themeStyle.axisLabelFont)
                        : visibleLabel.originalText;
                    visibleLabel.size = measureText(visibleLabel.text, this.labelStyle, chart.themeStyle.axisLabelFont);
                }
                const width = isAxisLabelBreak
                    ? visibleLabel.breakLabelSize.width
                    : visibleLabel.size.width;
                const height = isAxisLabelBreak
                    ? visibleLabel.breakLabelSize.height
                    : visibleLabel.size.height;
                if (width > this.maxLabelSize.width) {
                    this.maxLabelSize.width = width;
                    this.rotatedLabel = typeof visibleLabel.text === 'string'
                        ? visibleLabel.text
                        : visibleLabel.text.join('<br>');
                }
                if (height > this.maxLabelSize.height) {
                    this.maxLabelSize.height = height;
                }
            }
        }
        if (this.angle !== 0 && this.orientation === 'Horizontal') {
            //I264474: Fix for datasource bind im mounted console error ocurred
            this.rotatedLabel = isNullOrUndefined(this.rotatedLabel) ? '' : this.rotatedLabel;
            const isHorizontalAngle = this.angle === -360 || this.angle === 0 || this.angle === -180 ||
                this.angle === 180 || this.angle === 360;
            // To avoid overlap axis label with chart title or chart legend when it is outside.
            this.maxLabelSize = rotateTextSize(this.labelStyle, this.rotatedLabel, this.angle, chart, chart.themeStyle.axisLabelFont);
        }
        else if (this.angle !== 0 && this.orientation === 'Vertical') {
            //I264474: Fix for datasource bind im mounted console error ocurred
            this.rotatedLabel = isNullOrUndefined(this.rotatedLabel) ? '' : this.rotatedLabel;
            const isHorizontalAngle = this.angle === -360 || this.angle === 0 || this.angle === -180 ||
                this.angle === 180 || this.angle === 360;
            // To avoid overlap axis label with chart title or chart legend when it is outside.
            this.maxLabelSize = rotateTextSize(this.labelStyle, this.rotatedLabel, this.angle, chart, chart.themeStyle.axisLabelFont);
        }
        if (chart.multiLevelLabelModule && this.multiLevelLabels.length > 0) {
            chart.multiLevelLabelModule.getMultilevelLabelsHeight(this);
        }
    }
    getMaxTemplateLabelWidth(chart) {
        let pointX;
        let previousEnd = 0;
        let isIntersect = false;
        const defaultLabelHeight = 15.96;
        const action = this.labelIntersectAction;
        const isRotateWithTrim = action === 'Rotate45WithTrim' || action === 'Rotate90WithTrim';
        let label;
        for (let i = 0, len = this.visibleLabels.length; i < len; i++) {
            label = this.visibleLabels[i];
            const childElement = createTemplate(createElement('div', {
                id: '_AxisLabelTemplate_',
                styles: 'position:absolute; cursor: default; white-space: nowrap;pointer-events:none; z-index:1;'
            }), i, this.labelTemplate, chart, label);
            const elementRect = measureElementRect(childElement, chart.redraw);
            const width = elementRect.width;
            const height = elementRect.height;
            label.size = new Size(width, height);
            if (width > this.maxLabelSize.width) {
                this.maxLabelSize.width = width;
            }
            if (height > this.maxLabelSize.height) {
                this.maxLabelSize.height = height;
            }
            if (action === 'None' || action === 'Hide') {
                continue;
            }
            if (this.orientation === 'Horizontal' && this.rect.width > 0 && !isIntersect) {
                const width1 = label.size.width;
                const availableLabelWidth = this.labelPlacement === 'BetweenTicks' || this.visibleLabels.length === 1
                    ? this.rect.width / this.visibleLabels.length
                    : this.rect.width / (this.visibleLabels.length - 1);
                const isLabelWidthExceeded = width1 > availableLabelWidth;
                pointX = (valueToCoefficient(label.value, this) * this.rect.width) + this.rect.x;
                pointX -= width1 / 2;
                if (this.edgeLabelPlacement === 'Shift') {
                    if (i === 0 && pointX < this.rect.x) {
                        pointX = this.rect.x;
                    }
                    if (i === this.visibleLabels.length - 1 && ((pointX + width1) > (this.rect.x + this.rect.width))) {
                        pointX = this.rect.x + this.rect.width - width1;
                    }
                }
                const isLabelIntersect = i > 0 && (!this.isAxisInverse ? pointX <= previousEnd
                    : pointX + width1 >= previousEnd);
                switch (action) {
                    case 'Rotate45':
                    case 'Rotate90':
                        if (i > 0 && (!this.isAxisInverse ? pointX <= previousEnd : pointX + width1 >= previousEnd)) {
                            this.angle = (action === 'Rotate45') ? 45 : 90;
                            isIntersect = true;
                        }
                        break;
                    case 'Rotate45WithTrim':
                    case 'Rotate90WithTrim':
                        if (isLabelWidthExceeded || isLabelIntersect) {
                            this.angle = action === 'Rotate45WithTrim' ? 45 : 90;
                            isIntersect = true;
                        }
                        break;
                }
                previousEnd = this.isAxisInverse ? pointX : pointX + width1;
            }
        }
        if (isRotateWithTrim && isIntersect && this.angle !== 0 && this.orientation === 'Horizontal'
            && this.rect.width > 0 && this.visibleLabels.length > 0) {
            const intervalLength = this.rect.width / this.visibleLabels.length;
            this.maxLabelSize = new Size(0, 0);
            for (const visibleLabel of this.visibleLabels) {
                visibleLabel.size.width = Math.min(visibleLabel.size.width, intervalLength);
                this.maxLabelSize.width = Math.max(this.maxLabelSize.width, visibleLabel.size.width);
                this.maxLabelSize.height = Math.max(this.maxLabelSize.height, visibleLabel.size.height);
            }
        }
        this.maxLabelSize.height = this.visibleLabels.length === 0 && this.crosshairTooltip.enable ?
            defaultLabelHeight : this.maxLabelSize.height;
        if (this.rect.height !== 0) {
            this.maxLabelSize.height = Math.min(this.maxLabelSize.height, (this.rect.height / 2));
        }
        if (this.rect.width !== 0) {
            this.maxLabelSize.width = Math.min(this.maxLabelSize.width, (this.rect.width / 2));
        }
        if (this.angle !== 0 && this.orientation === 'Horizontal') {
            const angleInRad = this.angle * (Math.PI / 180);
            const width = this.maxLabelSize.width;
            const height = this.maxLabelSize.height;
            this.maxLabelSize = new Size(Math.abs(width * Math.cos(angleInRad)) + Math.abs(height * Math.sin(angleInRad)), Math.abs(width * Math.sin(angleInRad)) + Math.abs(height * Math.cos(angleInRad)));
        }
        if (chart.multiLevelLabelModule && this.multiLevelLabels.length > 0) {
            chart.multiLevelLabelModule.getMultilevelLabelsHeight(this);
        }
    }
    /**
     * Finds the multiple rows for axis.
     *
     * @returns {void}
     */
    findMultiRows(length, currentX, currentLabel, isBreakLabels) {
        let label;
        let pointX;
        let width2;
        const store = [];
        let isMultiRows;
        for (let i = length - 1; i >= 0; i--) {
            label = this.visibleLabels[i];
            width2 = isBreakLabels ? label.breakLabelSize.width : label.size.width;
            pointX = (valueToCoefficient(label.value, this) * this.rect.width) + this.rect.x;
            isMultiRows = !this.isAxisInverse ? currentX < (pointX + width2 * 0.5) :
                currentX + currentLabel.size.width > (pointX - width2 * 0.5);
            if (isMultiRows) {
                store.push(label.index);
                currentLabel.index = (currentLabel.index > label.index) ? currentLabel.index : label.index + 1;
            }
            else {
                currentLabel.index = store.indexOf(label.index) > -1 ? currentLabel.index : label.index;
            }
        }
        const height = ((isBreakLabels ? currentLabel.breakLabelSize.height : currentLabel.size.height) * currentLabel.index) +
            (5 * (currentLabel.index - 1));
        if (height > this.maxLabelSize.height) {
            this.maxLabelSize.height = height;
        }
    }
    /**
     * Finds the default module for axis.
     *
     * @returns {void}
     * @private
     */
    getModule(chart) {
        if (this.valueType === 'Double') {
            this.baseModule = new Double(chart);
        }
        else {
            this.baseModule = chart[firstToLowerCase(this.valueType) + 'Module'];
        }
    }
    /**
     * Set the axis `opposedPosition` and `isInversed` properties.
     *
     * @param {boolean} isPolar - Indicates whether the axis is polar or not.
     * @returns {void}
     * @private
     */
    setIsInversedAndOpposedPosition(isPolar = false) {
        this.isAxisOpposedPosition = this.opposedPosition || (!isPolar && this.isRTLEnabled && this.orientation === 'Vertical');
        if (this.opposedPosition && (!isPolar && this.isRTLEnabled && this.orientation === 'Vertical')) {
            this.isAxisOpposedPosition = false;
        }
        this.isAxisInverse = this.isInversed || (this.isRTLEnabled && this.orientation === 'Horizontal');
        if (this.isInversed && (!isPolar && this.isRTLEnabled && this.orientation === 'Horizontal')) {
            this.isAxisInverse = false;
        }
    }
    /**
     * Updates the axis within the chart.
     *
     * @returns {void}
     * @private
     */
    updateAxis() {
        const chart = this.baseModule.chart;
        const chartAxisLayoutPanel = chart.chartAxisLayoutPanel;
        const index = chart.axisCollections.indexOf(this);
        const axisElement = getElement(chart.element.id + 'AxisInsideCollection');
        const axisLineElement = getElement(chart.element.id + 'AxisOutsideCollection');
        chartAxisLayoutPanel.element = chart.renderer.createGroup({ id: chart.element.id + 'AxisGroup' + index + 'Inside' });
        const outsideElement = chart.renderer.createGroup({ id: chart.element.id + 'AxisGroup' + index + 'Outside' });
        const isInside = chartAxisLayoutPanel.findAxisPosition(this);
        chartAxisLayoutPanel.drawAxis(this, index, isInside, outsideElement, axisElement, axisLineElement);
        if (!chart.enableCanvas) {
            appendChildElement(chart.enableCanvas, axisElement, chartAxisLayoutPanel.element, chart.redraw);
        }
    }
    /**
     * Used to collect the rect of axes, to check whether they collide with series names or not.
     *
     * @param {Chart} chart - The current chart instance.
     * @returns {void}
     * @private
     */
    registerAxisLabelRects(chart) {
        if (!this.visible || !this.internalVisibility || !this.rect || !this.visibleLabels.length) {
            return;
        }
        const padding = this.labelPadding;
        const tick = this.majorTickLines.height;
        const push = (rect) => { chart.axisLabelCollections.push(rect); };
        for (let i = 0; i < this.visibleLabels.length; i++) {
            const label = this.visibleLabels[i];
            if (!label || !label.size) {
                continue;
            }
            let x;
            let y;
            const width = label.size.width;
            const height = label.size.height;
            if (this.orientation === 'Horizontal') {
                x = (valueToCoefficient(label.value, this) * this.rect.width) + this.rect.x - width / 2;
                if (this.edgeLabelPlacement === 'Shift') {
                    if (i === 0 && x < this.rect.x) {
                        x = this.rect.x;
                    }
                    if (i === this.visibleLabels.length - 1 && (x + width) > (this.rect.x + this.rect.width)) {
                        x = this.rect.x + this.rect.width - width;
                    }
                }
                if (this.labelPosition === 'Inside') {
                    y = this.isAxisOpposedPosition
                        ? (this.rect.y + padding) // inside near top of axis rect
                        : (this.rect.y + this.rect.height - height - padding);
                }
                else {
                    y = this.isAxisOpposedPosition
                        ? (this.rect.y - tick - padding - height) // above axis
                        : (this.rect.y + this.rect.height + tick + padding); // below axis
                }
            }
            else {
                y = (valueToCoefficient(label.value, this) * this.rect.height) + this.rect.y - height / 2;
                if (this.labelPosition === 'Inside') {
                    x = this.isAxisOpposedPosition
                        ? (this.rect.x + this.rect.width - width - padding) // inside near right
                        : (this.rect.x + padding); // inside near left
                }
                else {
                    x = this.isAxisOpposedPosition
                        ? (this.rect.x + this.rect.width + tick + padding) // right of axis
                        : (this.rect.x - tick - padding - width); // left of axis
                }
            }
            push(new Rect(x, y, width, height));
        }
    }
}
__decorate([
    Complex({ fontFamily: null, size: '12px', fontStyle: 'Normal', fontWeight: '400', color: null }, Font)
], Axis.prototype, "labelStyle", void 0);
__decorate([
    Complex({}, CrosshairTooltip)
], Axis.prototype, "crosshairTooltip", void 0);
__decorate([
    Property('')
], Axis.prototype, "title", void 0);
__decorate([
    Complex({ fontFamily: null, size: null, fontStyle: null, fontWeight: null, color: null }, Font)
], Axis.prototype, "titleStyle", void 0);
__decorate([
    Property('')
], Axis.prototype, "labelFormat", void 0);
__decorate([
    Property('')
], Axis.prototype, "skeleton", void 0);
__decorate([
    Property('DateTime')
], Axis.prototype, "skeletonType", void 0);
__decorate([
    Property('Center')
], Axis.prototype, "lineBreakAlignment", void 0);
__decorate([
    Property(0)
], Axis.prototype, "plotOffset", void 0);
__decorate([
    Property(null)
], Axis.prototype, "plotOffsetLeft", void 0);
__decorate([
    Property(null)
], Axis.prototype, "plotOffsetTop", void 0);
__decorate([
    Property(null)
], Axis.prototype, "plotOffsetRight", void 0);
__decorate([
    Property(null)
], Axis.prototype, "plotOffsetBottom", void 0);
__decorate([
    Property(false)
], Axis.prototype, "isIndexed", void 0);
__decorate([
    Property(10)
], Axis.prototype, "logBase", void 0);
__decorate([
    Property(0)
], Axis.prototype, "columnIndex", void 0);
__decorate([
    Property(0)
], Axis.prototype, "rowIndex", void 0);
__decorate([
    Property(1)
], Axis.prototype, "span", void 0);
__decorate([
    Property(null)
], Axis.prototype, "desiredIntervals", void 0);
__decorate([
    Property(3)
], Axis.prototype, "maximumLabels", void 0);
__decorate([
    Property(1)
], Axis.prototype, "zoomFactor", void 0);
__decorate([
    Property(0)
], Axis.prototype, "zoomPosition", void 0);
__decorate([
    Property(true)
], Axis.prototype, "enableScrollbarOnZooming", void 0);
__decorate([
    Property(false)
], Axis.prototype, "opposedPosition", void 0);
__decorate([
    Property(true)
], Axis.prototype, "enableAutoIntervalOnZooming", void 0);
__decorate([
    Property('Auto')
], Axis.prototype, "rangePadding", void 0);
__decorate([
    Property('Double')
], Axis.prototype, "valueType", void 0);
__decorate([
    Property('Shift')
], Axis.prototype, "edgeLabelPlacement", void 0);
__decorate([
    Property('Auto')
], Axis.prototype, "intervalType", void 0);
__decorate([
    Property('BetweenTicks')
], Axis.prototype, "labelPlacement", void 0);
__decorate([
    Property('Outside')
], Axis.prototype, "tickPosition", void 0);
__decorate([
    Property('Outside')
], Axis.prototype, "labelPosition", void 0);
__decorate([
    Property(null)
], Axis.prototype, "labelTemplate", void 0);
__decorate([
    Property('')
], Axis.prototype, "name", void 0);
__decorate([
    Property(true)
], Axis.prototype, "visible", void 0);
__decorate([
    Property(0)
], Axis.prototype, "minorTicksPerInterval", void 0);
__decorate([
    Property(0)
], Axis.prototype, "labelRotation", void 0);
__decorate([
    Property(null)
], Axis.prototype, "titleRotation", void 0);
__decorate([
    Property(null)
], Axis.prototype, "crossesAt", void 0);
__decorate([
    Property(true)
], Axis.prototype, "placeNextToAxisLine", void 0);
__decorate([
    Property(null)
], Axis.prototype, "crossesInAxis", void 0);
__decorate([
    Property(null)
], Axis.prototype, "minimum", void 0);
__decorate([
    Property(null)
], Axis.prototype, "maximum", void 0);
__decorate([
    Property(null)
], Axis.prototype, "interval", void 0);
__decorate([
    Property(34)
], Axis.prototype, "maximumLabelWidth", void 0);
__decorate([
    Property(false)
], Axis.prototype, "enableTrim", void 0);
__decorate([
    Property(false)
], Axis.prototype, "enableWrap", void 0);
__decorate([
    Property(5)
], Axis.prototype, "labelPadding", void 0);
__decorate([
    Property(5)
], Axis.prototype, "titlePadding", void 0);
__decorate([
    Complex({}, MajorTickLines)
], Axis.prototype, "majorTickLines", void 0);
__decorate([
    Complex({}, MinorTickLines)
], Axis.prototype, "minorTickLines", void 0);
__decorate([
    Complex({}, MajorGridLines)
], Axis.prototype, "majorGridLines", void 0);
__decorate([
    Complex({}, MinorGridLines)
], Axis.prototype, "minorGridLines", void 0);
__decorate([
    Complex({}, AxisLine)
], Axis.prototype, "lineStyle", void 0);
__decorate([
    Property(Browser.isDevice ? 'Rotate45' : 'Trim')
], Axis.prototype, "labelIntersectAction", void 0);
__decorate([
    Property(false)
], Axis.prototype, "isInversed", void 0);
__decorate([
    Property(100)
], Axis.prototype, "coefficient", void 0);
__decorate([
    Property(0)
], Axis.prototype, "startAngle", void 0);
__decorate([
    Property(true)
], Axis.prototype, "startFromZero", void 0);
__decorate([
    Property(null)
], Axis.prototype, "description", void 0);
__decorate([
    Property(2)
], Axis.prototype, "tabIndex", void 0);
__decorate([
    Collection([], StripLineSettings)
], Axis.prototype, "stripLines", void 0);
__decorate([
    Collection([], MultiLevelLabels)
], Axis.prototype, "multiLevelLabels", void 0);
__decorate([
    Complex({ color: null, width: 0, type: 'Rectangle' }, LabelBorder)
], Axis.prototype, "border", void 0);
__decorate([
    Complex({}, ScrollbarSettings)
], Axis.prototype, "scrollbarSettings", void 0);
/** @private */
export class VisibleLabels {
    constructor(text, value, labelStyle, originalText, size = new Size(0, 0), breakLabelSize = new Size(0, 0), index = 1) {
        this.text = text;
        this.originalText = originalText;
        this.value = value;
        this.labelStyle = labelStyle;
        this.size = size;
        this.breakLabelSize = breakLabelSize;
        this.index = index;
    }
}
